'use client'

import { C, Callout, Exercise, H, Lesson, M, MB, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/numbers-extra'

export default function ProjectGrowth() {
  return (
    <Lesson>
      <Step>
        <H>Project: how fast is it growing?</H>
        <P>
          “AI is growing exponentially” is something you’ll hear a lot. In this project you’ll build the tools to check claims
          like that yourself, using everything from this module: formulas, rearranging, exponents, logs and big numbers.
        </P>
        <OL>
          <li>Simulate two kinds of growth and see how differently they behave.</li>
          <li>Work out how long something takes to double — and when it passes a target.</li>
          <li>Measure a growth rate from data, and use it to predict the future.</li>
        </OL>
      </Step>

      <Step gate="growth-sim">
        <H>Part 1 · Adding vs. multiplying</H>
        <P>
          <strong>Linear</strong> growth adds the same amount every year. <strong>Exponential</strong> (or compound) growth
          multiplies by the same amount every year. Early on they look alike. Later they don’t.
        </P>
        <MB>{'\\text{linear: } v_t = v_0 + s\\,t \\qquad\\qquad \\text{compound: } v_t = v_0\\,(1 + r)^t'}</MB>
        <Exercise
          id="growth-sim"
          title="linear and compound"
          {...EX.growthSim}
          hints={[
            <>
              <C>{'range(years + 1).map(t => start + step * t)'}</C>
            </>,
            <>
              Start with <C>{'const out = [start]'}</C>, and each year push the previous value times <C>(1 + rate)</C>.
            </>,
          ]}
        />
        <Callout kind="try">
          In the chart, linear is ahead for the first few years. Find the year compound growth overtakes it. Then try 10% a year
          instead of 25%.
        </Callout>
      </Step>

      <Step gate="doubling">
        <H>Part 2 · Doubling time</H>
        <P>
          Exponential growth has a constant <strong>doubling time</strong>: however big it already is, it takes the same time to
          double again. Finding it means solving <M>{'(1 + r)^t = 2'}</M> for <M>t</M>. That’s exactly what logarithms are for.
        </P>
        <MB>{'t = \\frac{\\log 2}{\\log(1 + r)}'}</MB>
        <Exercise
          id="doubling"
          title="doublingTime and yearsUntil"
          {...EX.doubling}
          hints={[
            <>
              <C>{'Math.log(2) / Math.log(1 + rate)'}</C>
            </>,
            <>
              Solve <M>{'\\text{start}\\cdot(1+r)^t = \\text{target}'}</M>: divide by start, take logs, divide by{' '}
              <M>{'\\log(1 + r)'}</M>, then round up with <C>Math.ceil</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="fit-growth">
        <H>Part 3 · Read the trend off the data</H>
        <P>
          Real data is noisy, but there’s a trick: exponential growth becomes a <strong>straight line</strong> when you take its
          log. That’s why charts of AI progress use log axes. On a log axis, a straight line means steady exponential growth.
        </P>
        <Exercise
          id="fit-growth"
          title="growthFactor and predict"
          {...EX.fitGrowth}
          hints={[
            <>
              <C>{'(values[n - 1] / values[0]) ** (1 / (n - 1))'}</C> — a fractional power is a root: <M>{'x^{1/7}'}</M> is the
              7th root.
            </>,
            <>
              <C>{'values[values.length - 1] * growthFactor(values) ** yearsAhead'}</C>
            </>,
          ]}
        />
        <Callout kind="warn">
          Extrapolating is dangerous. ×3 a year for 20 more years would mean a 3-billion-fold increase. Real growth eventually
          runs into limits: money, energy, chips, data. Always ask what could stop a trend.
        </Callout>
      </Step>

      <Step>
        <H>Stretch goals</H>
        <UL>
          <li>
            Use only the <em>first</em> and <em>last</em> values, and a single noisy reading can skew the estimate. Instead,
            compute the factor between every pair of neighbouring years and average them. Do you get a different answer?
          </li>
          <li>
            Use <C>solveBySearch</C> from the Algebra lesson to find the exact moment (a fraction of a year) when{' '}
            <C>{'100 * 1.25 ** t'}</C> overtakes <C>{'100 + 200 * t'}</C>. Hint: search between t = 10 and t = 20 for where their difference crosses 0.
          </li>
          <li>Plot your prediction for the next 5 years next to the data, on both a normal and a log scale.</li>
        </UL>
      </Step>
    </Lesson>
  )
}
